import { ConditionComparisonChainOperator } from "@src/__generated__/graphql";
import { QuestionIdToQuestionConfigurationMap } from "@src/pages/intake-requests/new/useNewIntakeRequest";
import { CreateIntakeSubmissionSchema } from "@src/sudozi-schemas/intake-submission-schemas";
import { isStringArray } from "@src/utils/array";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

function anyOfPredicate(
  anyOf: QuestionIdToQuestionConfigurationMap[string]["conditionAnyOfs"][0],
  questionIdToValueMap: QuestionIdToValueMap,
) {
  const respectiveFieldValue = questionIdToValueMap[anyOf.valuesQuestionId];
  if (respectiveFieldValue === null || respectiveFieldValue === undefined) {
    return false;
  }

  // We only want string or string[] because those are the value types for select menus
  if (
    typeof respectiveFieldValue !== "string" &&
    !Array.isArray(respectiveFieldValue)
  ) {
    return false;
  }

  const passes = anyOf.values.some((v) =>
    typeof respectiveFieldValue === "string"
      ? v === respectiveFieldValue
      : isStringArray(respectiveFieldValue)
      ? respectiveFieldValue.includes(v)
      : respectiveFieldValue.some((value) => value.id === v),
  );
  return passes;
}

function numericPredicate(
  numeric: QuestionIdToQuestionConfigurationMap[string]["conditionNumerics"][0],
  questionIdToValueMap: QuestionIdToValueMap,
) {
  const respectiveFieldValue = questionIdToValueMap[numeric.valuesQuestionId];
  if (respectiveFieldValue === null || respectiveFieldValue === undefined) {
    return false;
  }

  // its not always a number, its an object if currency is enabled
  if (
    typeof respectiveFieldValue === "object" &&
    "value" in respectiveFieldValue &&
    typeof respectiveFieldValue.value === "number"
  ) {
    const val = respectiveFieldValue;
    if (
      typeof numeric.minimumValue === "number" &&
      Number(val.value) < numeric.minimumValue
    ) {
      return false;
    }
    if (
      typeof numeric.maximumValue === "number" &&
      Number(val.value) > numeric.maximumValue
    ) {
      return false;
    }
  } else if (typeof respectiveFieldValue === "number") {
    if (
      typeof numeric.minimumValue === "number" &&
      respectiveFieldValue < numeric.minimumValue
    ) {
      return false;
    }
    if (
      typeof numeric.maximumValue === "number" &&
      respectiveFieldValue > numeric.maximumValue
    ) {
      return false;
    }
  }

  return true;
}

function booleanPredicate(
  boolean: QuestionIdToQuestionConfigurationMap[string]["conditionBooleans"][0],
  questionIdToValueMap: QuestionIdToValueMap,
) {
  const respectiveFieldValue = questionIdToValueMap[boolean.valuesQuestionId];
  if (respectiveFieldValue === null || respectiveFieldValue === undefined) {
    return false;
  }

  if (typeof respectiveFieldValue !== "boolean") {
    console.error(
      `Expected field value to be a boolean but got ${typeof respectiveFieldValue}`,
    );
    return false;
  }

  const passes = respectiveFieldValue === boolean.value;
  return passes;
}

export function shouldShowField(
  questionIdToConditionallyShow: string,
  questionIdToQuestionConfigurationMap: QuestionIdToQuestionConfigurationMap,
  questionIdToValueMap: QuestionIdToValueMap,
) {
  const configuration =
    questionIdToQuestionConfigurationMap[questionIdToConditionallyShow];

  // invalid state
  if (!configuration) {
    return false;
  }

  if (
    configuration.conditionAnyOfs.length +
      configuration.conditionNumerics.length +
      configuration.conditionBooleans.length ===
    0
  ) {
    return true;
  }

  if (configuration.chainOperator === ConditionComparisonChainOperator.AND) {
    return (
      configuration.conditionAnyOfs.every((anyOf) =>
        anyOfPredicate(anyOf, questionIdToValueMap),
      ) &&
      configuration.conditionNumerics.every((numeric) =>
        numericPredicate(numeric, questionIdToValueMap),
      ) &&
      configuration.conditionBooleans.every((boolean) =>
        booleanPredicate(boolean, questionIdToValueMap),
      )
    );
  }

  return (
    configuration.conditionAnyOfs.some((anyOf) =>
      anyOfPredicate(anyOf, questionIdToValueMap),
    ) ||
    configuration.conditionNumerics.some((numeric) =>
      numericPredicate(numeric, questionIdToValueMap),
    ) ||
    configuration.conditionBooleans.some((boolean) =>
      booleanPredicate(boolean, questionIdToValueMap),
    )
  );
}

export type QuestionIdToValueMap = {
  [
    id: string
  ]: CreateIntakeSubmissionSchema["steps"][number]["fields"][number]["value"];
};

export const useIntakeForm = (defaultValues: CreateIntakeSubmissionSchema) => {
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    trigger,
    getValues,
    setValue,
    reset,
  } = useForm<CreateIntakeSubmissionSchema>({
    defaultValues,
  });

  const [shouldShowSubmissionModal, setShouldShowSubmissionModal] =
    useState(false);

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const questionIdToValueMap = watch("steps").reduce<QuestionIdToValueMap>(
    (map, step) => {
      step.fields.forEach((field) => {
        map[field.questionId] = field.value;
      });

      return map;
    },
    {},
  );

  return {
    control,
    handleSubmit,
    errors,
    trigger,
    getValues,
    questionIdToValueMap,
    watch,
    reset,
    isDirty,
    setValue,
    setShouldShowSubmissionModal,
    shouldShowSubmissionModal,
  };
};
